import { Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, finalize, map, Observable, tap } from 'rxjs';
import { RemindersApiProviderService } from '../../data-providers/api-providers/reminders-api-provider/reminders-api-provider.service';
import { EInfiniteScrollApiKey } from '../../services/infinite-scrolling/infinite-scrolling.consts';
import { InfiniteScrollingService } from '../../services/infinite-scrolling/infinite-scrolling.service';
import { QuickReminderFilterService } from '../filters/filters-api/reminders-search/quick-reminder-filter.service';
import { TSearchResponseDTO } from '../points/point-search/point-search.consts';
import { TReminderSearchResponse } from './reminder-search.consts';
import { TReminder } from './reminders.consts';

@Injectable({
  providedIn: 'root',
})
export class ReminderSearchService {
  private data: TReminder[] = [];
  private apiKey: string = EInfiniteScrollApiKey.REMINDERS;
  private apiLoading$ = new BehaviorSubject<boolean>(false);

  constructor(
    private infiniteScrollingService: InfiniteScrollingService,
    private quickReminderFilterService: QuickReminderFilterService,
    private remindersApiProviderService: RemindersApiProviderService,
  ) {}

  get apiLoadingStatus$(): Observable<boolean> {
    return this.apiLoading$.asObservable();
  }

  getData(workspaceId: string): TReminder[] {
    if (!workspaceId) {
      return;
    }
    return this.data?.[workspaceId] ?? [];
  }

  searchReminders(seriesId?: string): Observable<TReminder[]> {
    const isLoading = this.infiniteScrollingService.getRequestData(this.apiKey).loading;

    if (isLoading) {
      return EMPTY;
    }

    const query: TSearchResponseDTO = this.getReminderSearchQuery(seriesId);

    this.apiLoading$.next(true);

    const request = (body: TSearchResponseDTO): Observable<TReminderSearchResponse> =>
      this.remindersApiProviderService.searchReminders(body);

    return this.infiniteScrollingService
      .fetchData<TReminderSearchResponse>(this.apiKey, request, query, true)
      .pipe(
        tap((response) => {
          this.storeRemindersData(response.data);
          this.apiLoading$.next(false);
        }),
        map((response) => response.data),
        finalize(() => {
          this.infiniteScrollingService.getRequestData(this.apiKey).loading = false;
          this.apiLoading$.next(false);
        }),
      );
  }

  private getReminderSearchQuery(seriesId?: string): TSearchResponseDTO {
    return {
      pagination: {
        cursor: null,
        pageSize: 100000,
      },
      filter: this.quickReminderFilterService.getFiltersTransformed(seriesId),
    };
  }

  private storeRemindersData(response: TReminder[]): void {
    this.data = response;
  }
}
